import React, { useEffect, useState } from "react";
import axios from "axios";
import { config } from "../../config";
import {
  Row,
  Col,
  Card,
  Typography,
  Spin,
  Alert,
  Input,
  Button,
  Modal,
  message,
} from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { fetchSharedData } from "../../utils/sharedFetcher";
import * as pdfjsLib from "pdfjs-dist";
import pdfjsWorker from "pdfjs-dist/build/pdf.worker.entry";
import "./Publikasi.scss";

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

const { Title, Text } = Typography;
const { Search } = Input;

const CACHE_KEY = "publikasi_thumbnails_cache";
const CACHE_DURATION = 5 * 60 * 1000;

const loadCachedThumbnails = () => {
  const raw = localStorage.getItem(CACHE_KEY);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw);
    const now = Date.now();

    if (now - parsed.timestamp > CACHE_DURATION) {
      localStorage.removeItem(CACHE_KEY);
      return null;
    }

    return parsed.thumbs || {};
  } catch (e) {
    console.error("Failed to parse cache:", e);
    localStorage.removeItem(CACHE_KEY);
    return null;
  }
};

const saveThumbnailsToCache = (thumbsMap) => {
  const payload = {
    timestamp: Date.now(),
    thumbs: thumbsMap,
  };
  localStorage.setItem(CACHE_KEY, JSON.stringify(payload));
};

const Publikasi = () => {
  const [data, setData] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [thumbnails, setThumbnails] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [keyword, setKeyword] = useState("");
  const [pdfModalVisible, setPdfModalVisible] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);

  const getPdfThumbnail = async (pdfUrl) => {
    try {
      const loadingTask = pdfjsLib.getDocument(pdfUrl);
      const pdf = await loadingTask.promise;
      const page = await pdf.getPage(1);
      
      // Gunakan scale kecil (0.3 - 0.5) agar string Base64 tidak terlalu panjang
      const viewport = page.getViewport({ scale: 0.4 }); 
      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d");
      canvas.width = viewport.width;
      canvas.height = viewport.height;

      await page.render({ canvasContext: context, viewport }).promise;

      // Simpan sebagai JPEG (lebih ringan dari PNG) untuk localStorage
      return canvas.toDataURL("image/jpeg", 0.7); 
    } catch (error) {
      console.error("Gagal generate thumbnail:", error);
      return null;
    }
  };

  useEffect(() => {
    const fetchPublikasi = async () => {
      try {
        const currentURL = window.location.href;
        const res = await fetchSharedData(
          `${config.BASE_URL}organization/findContentOrganization?namaDomain=${currentURL}`
        );

        const publikasiList = res.data?.data?.publikasi || [];
        if (publikasiList.length === 0) {
          setError("Belum ada publikasi.");
          setLoading(false);
          return;
        }

        setData(publikasiList);
        setFiltered(publikasiList);

        // --- LOGIC CACHE PROGRESIF ---
        const cachedThumbs = loadCachedThumbnails() || {};
        
        // 1. Tampilkan yang ada di cache dulu biar instan
        setThumbnails(cachedThumbs);
        setLoading(false); // Langsung matikan loading utama setelah data teks muncul

        // 2. Cari mana yang belum punya thumbnail
        const updatedThumbs = { ...cachedThumbs };
        let hasNewThumb = false;

        // 3. Proses satu per satu (tidak menunggu semua selesai)
        for (const item of publikasiList) {
          const id = item.publikasiDesaCantikId;
          
          if (!updatedThumbs[id] && item.file) {
            const pdfUrl = `${config.BASE_URL}getReferenceFile?pathFile=${encodeURIComponent(item.file)}`;
            const thumb = await getPdfThumbnail(pdfUrl);
            
            if (thumb) {
              updatedThumbs[id] = thumb;
              hasNewThumb = true;
              
              // Update state secara real-time per gambar yang selesai
              setThumbnails((prev) => ({
                ...prev,
                [id]: thumb,
              }));
            }
          }
        }

        // 4. Simpan ke cache jika ada update
        if (hasNewThumb) {
          saveThumbnailsToCache(updatedThumbs);
        }
        
      } catch (err) {
        setError("Gagal memuat data.");
        setLoading(false);
      }
    };

    fetchPublikasi();
  }, []);

  const onSearch = (value) => {
    setKeyword(value);
    const lower = value.toLowerCase();
    const result = data.filter((item) =>
      item.judul?.toLowerCase().includes(lower)
    );
    setFiltered(result);
  };

  const downloadPdf = (url, judul) => {
    const link = document.createElement("a");
    link.href = url;
    const safeFilename = `${judul}`.replace(/[<>:"/\\|?*]+/g, "").trim();
    link.setAttribute("download", `${safeFilename || "Publikasi"}.pdf`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const previewPdf = (id, file, judul) => {
    const cachedPreview = thumbnails[id];
    const fullUrl = `${config.BASE_URL}getReferenceFile?pathFile=${encodeURIComponent(file)}`;
    
    // Simpan data ke state untuk digunakan di dalam modal
    setPdfBlobUrl({
      url: fullUrl,
      preview: cachedPreview, // Gunakan Base64 dari cache
      judul: judul
    });
    setPdfModalVisible(true);
  };

  return (
    <div className="publikasi-wrapper">
      <Title level={2} className="publikasi-title">
        Publikasi Desa Cantik
      </Title>

      <div className="publikasi-search">
        <Search
          placeholder="Cari berdasarkan judul..."
          allowClear
          enterButton={<Button type="primary">Cari</Button>}
          value={keyword}
          onChange={(e) => onSearch(e.target.value)}
        />
      </div>

      {loading ? (
        <div className="publikasi-loading">
          <Spin
            indicator={<LoadingOutlined className="orange-spin" spin />}
            size="large"
          />
        </div>
      ) : error ? (
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          style={{ maxWidth: 600, margin: "0 auto" }}
        />
      ) : (
        <Row gutter={[24, 24]} align="stretch">
          {filtered.length === 0 ? (
            <Col span={24} className="publikasi-empty">
              <Text type="secondary">Tidak ada publikasi ditemukan.</Text>
            </Col>
          ) : (
            filtered.map((item) => {
              const cover = thumbnails[item.publikasiDesaCantikId];
              return (
                <Col key={item.publikasiDesaCantikId} xs={24} sm={12} md={8} style={{ display: 'flex' }}>
                  <Card
                    hoverable
                    onClick={() =>
                      previewPdf(item.publikasiDesaCantikId, item.file, item.judul)
                    }
                    className="publikasi-card"
                    cover={
                      <div className="card-cover" style={{ background: '#f0f0f0', height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {cover ? (
                          <img src={cover} alt="PDF Cover" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        ) : (
                          <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
                        )}
                      </div>
                    }
                  >
                    <Title level={5} style={{ margin: "8px 0" }}>
                      {item.judul || "Judul tidak tersedia"}
                    </Title>
                    <div className="card-desc">
                      {(item.keterangan || "Tidak ada keterangan.").replace(
                        /<[^>]+>/g,
                        ""
                      )}
                    </div>

                    <Button
                      block
                      type="primary"
                      style={{ backgroundColor: '#FF6B35', borderColor: '#FF6B35', borderRadius: '6px' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        downloadPdf(
                          `${config.BASE_URL}getReferenceFile?pathFile=${encodeURIComponent(item.file)}`,
                          item.judul || "Publikasi"
                        );
                      }}
                    >
                      Unduh Publikasi
                    </Button>
                  </Card>
                </Col>
              );
            })
          )}
        </Row>
      )}

      <Modal
        title={`Preview: ${pdfBlobUrl?.judul || "Publikasi"}`}
        open={pdfModalVisible}
        onCancel={() => {
          setPdfModalVisible(false);
          setPdfBlobUrl(null);
        }}
        footer={[
          <Button key="close" onClick={() => setPdfModalVisible(false)}>
            Tutup
          </Button>,
          <Button 
            key="download" 
            type="primary" 
            onClick={() => downloadPdf(pdfBlobUrl?.url, pdfBlobUrl?.judul)}
          >
            Unduh PDF Lengkap
          </Button>
        ]}
        width={700}
        centered
        destroyOnClose
      >
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          padding: '10px' 
        }}>
          {pdfBlobUrl?.preview ? (
            <img 
              src={pdfBlobUrl.preview} 
              alt="Preview" 
              style={{ 
                width: '100%', 
                height: 'auto', 
                borderRadius: '4px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)' 
              }} 
            />
          ) : (
            <div style={{ padding: '40px', textAlign: 'center' }}>
              <Spin indicator={<LoadingOutlined style={{ fontSize: 32 }} spin />} />
              <p style={{ marginTop: 12 }}>Menyiapkan preview...</p>
            </div>
          )}
          
          <div style={{ marginTop: 20, textAlign: 'center', color: '#8c8c8c' }}>
            <Text type="secondary">
              Menampilkan pratinjau halaman pertama. 
              <br />
              Silakan klik <b>Unduh</b> untuk membaca seluruh isi dokumen.
            </Text>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Publikasi;
